import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { errorMessage } from "./api";
import { qk } from "./queries";
import { isInFlight, type Entry } from "./types";
import { runUpload, validateFile, type UploadPhase } from "./upload";
import { useToasts } from "../components/Toasts";

/**
 * The upload queue and the set of entries currently developing.
 *
 * Both live here, at the root of the app, rather than in a route component:
 * uploads must keep running while the user wanders off to look at their
 * collections, and the developing cards must survive navigation.
 */

/**
 * `waiting` rather than `queued`: a file waiting its turn in *our* queue is a
 * different thing from an entry the server has queued for classification, and
 * they'd otherwise share a name and a caption.
 */
export type UploadStatus = "waiting" | "presigning" | "uploading" | "finalising" | "error";

export type UploadItem = {
  localId: string;
  /**
   * Known as soon as the presign comes back, well before the bytes land. Used
   * as the seed for the developing card so its rotation and mount don't change
   * when the card graduates from "uploading" to "developing".
   */
  entryId: string | null;
  name: string;
  size: number;
  /** A local object URL, shown while the real photograph is in the post. */
  previewUrl: string;
  /** 0..1 */
  progress: number;
  status: UploadStatus;
  error: string | null;
  /** False for files we rejected outright — retrying can't help. */
  retryable: boolean;
};

type UploadContextValue = {
  items: UploadItem[];
  /** entryId → local object URL, so a developing card can show the preview. */
  previews: Record<string, string>;
  /** Entry ids currently being polled towards a terminal status. */
  trackedIds: string[];
  enqueue: (files: FileList | readonly File[]) => void;
  retry: (localId: string) => void;
  cancel: (localId: string) => void;
  dismiss: (localId: string) => void;
  /** Start following an entry (also primes the query cache with it). */
  track: (entry: Entry) => void;
  /** Stop following it, and release its preview. */
  untrack: (entryId: string) => void;
};

const UploadContext = createContext<UploadContextValue | null>(null);

const MAX_CONCURRENT = 2;

function localId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `upload-${Math.random().toString(36).slice(2)}`;
}

export function UploadProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const { push } = useToasts();

  const [items, setItems] = useState<UploadItem[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [trackedIds, setTrackedIds] = useState<string[]>([]);

  // Files, previews and abort handles are kept in refs so the async runner
  // never has to reach into React state it might have closed over stale.
  const files = useRef(new Map<string, File>());
  const objectUrls = useRef(new Map<string, string>());
  const aborts = useRef(new Map<string, AbortController>());
  const pending = useRef<string[]>([]);
  const inFlight = useRef(0);
  const trackedPreviews = useRef(new Map<string, string>());

  const releaseLocal = useCallback((id: string) => {
    const url = objectUrls.current.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      objectUrls.current.delete(id);
    }
    files.current.delete(id);
    aborts.current.delete(id);
  }, []);

  const removeItem = useCallback(
    (id: string, options: { release: boolean }) => {
      if (options.release) releaseLocal(id);
      else {
        // The object URL now belongs to the developing card; only drop our
        // bookkeeping, not the URL itself.
        objectUrls.current.delete(id);
        files.current.delete(id);
        aborts.current.delete(id);
      }
      setItems((current) => current.filter((item) => item.localId !== id));
    },
    [releaseLocal],
  );

  const patchItem = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item) => (item.localId === id ? { ...item, ...patch } : item)),
    );
  }, []);

  const track = useCallback(
    (entry: Entry) => {
      // Seed the per-entry cache so polling starts with data in hand.
      client.setQueryData<Entry>(qk.entry(entry.id), entry);
      setTrackedIds((current) => (current.includes(entry.id) ? current : [entry.id, ...current]));
    },
    [client],
  );

  const untrack = useCallback((entryId: string) => {
    setTrackedIds((current) => current.filter((id) => id !== entryId));
    const url = trackedPreviews.current.get(entryId);
    if (url) {
      URL.revokeObjectURL(url);
      trackedPreviews.current.delete(entryId);
      setPreviews((current) => {
        if (!(entryId in current)) return current;
        const next = { ...current };
        delete next[entryId];
        return next;
      });
    }
  }, []);

  // The runner and the pump are mutually recursive: the pump starts uploads,
  // and each finished upload pumps again. A ref breaks the cycle without
  // making either callback unstable.
  const runOneRef = useRef<(id: string) => Promise<void>>(async () => {});

  const pump = useCallback(() => {
    while (inFlight.current < MAX_CONCURRENT && pending.current.length > 0) {
      const next = pending.current.shift();
      if (next === undefined) break;
      inFlight.current += 1;
      void runOneRef.current(next);
    }
  }, []);

  const runOne = useCallback(
    async (id: string) => {
      const file = files.current.get(id);
      if (!file) {
        removeItem(id, { release: true });
        return;
      }

      const controller = new AbortController();
      aborts.current.set(id, controller);

      try {
        const entry = await runUpload({
          file,
          signal: controller.signal,
          onPhase: (phase: UploadPhase) => patchItem(id, { status: phase }),
          onProgress: (fraction) => patchItem(id, { progress: fraction }),
          onEntryId: (entryId) => patchItem(id, { entryId }),
        });

        // Hand the local preview over to the developing card, then let the
        // tray forget about this file.
        const preview = objectUrls.current.get(id);
        if (preview) {
          trackedPreviews.current.set(entry.id, preview);
          setPreviews((current) => ({ ...current, [entry.id]: preview }));
        }
        track(entry);
        removeItem(id, { release: false });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          removeItem(id, { release: true });
          return;
        }
        const message = errorMessage(error, "The upload didn't go through.");
        patchItem(id, { status: "error", error: message, retryable: true });
        push({ kind: "problem", title: "That photograph didn't upload.", body: message });
      } finally {
        aborts.current.delete(id);
        inFlight.current = Math.max(0, inFlight.current - 1);
        pump();
      }
    },
    [patchItem, pump, push, removeItem, track],
  );

  runOneRef.current = runOne;

  const enqueue = useCallback(
    (incoming: FileList | readonly File[]) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;

      const created: UploadItem[] = [];
      const rejected: UploadItem[] = [];

      for (const file of list) {
        const id = localId();
        const problem = validateFile(file);

        if (problem) {
          rejected.push({
            localId: id,
            entryId: null,
            name: file.name,
            size: file.size,
            previewUrl: "",
            progress: 0,
            status: "error",
            error: problem,
            retryable: false,
          });
          continue;
        }

        const url = URL.createObjectURL(file);
        files.current.set(id, file);
        objectUrls.current.set(id, url);
        created.push({
          localId: id,
          entryId: null,
          name: file.name,
          size: file.size,
          previewUrl: url,
          progress: 0,
          status: "waiting",
          error: null,
          retryable: true,
        });
      }

      if (rejected.length > 0) {
        const first = rejected[0];
        push({
          kind: "caution",
          title:
            rejected.length === 1
              ? "One file was set aside."
              : `${rejected.length} files were set aside.`,
          ...(first?.error ? { body: first.error } : {}),
        });
      }

      setItems((current) => [...current, ...rejected, ...created]);
      pending.current.push(...created.map((item) => item.localId));
      pump();
    },
    [pump, push],
  );

  const retry = useCallback((id: string) => {
    if (!files.current.has(id)) return;
    setItems((current) =>
      current.map((item) =>
        item.localId === id ? { ...item, status: "waiting", progress: 0, error: null } : item,
      ),
    );
    pending.current.push(id);
    pump();
  }, [pump]);

  const cancel = useCallback(
    (id: string) => {
      const controller = aborts.current.get(id);
      if (controller) {
        controller.abort();
        return;
      }
      pending.current = pending.current.filter((queued) => queued !== id);
      removeItem(id, { release: true });
    },
    [removeItem],
  );

  const dismiss = useCallback(
    (id: string) => {
      pending.current = pending.current.filter((queued) => queued !== id);
      removeItem(id, { release: true });
    },
    [removeItem],
  );

  // Last resort: never leak an object URL when the app unmounts.
  useEffect(() => {
    const locals = objectUrls.current;
    const tracked = trackedPreviews.current;
    return () => {
      for (const url of locals.values()) URL.revokeObjectURL(url);
      for (const url of tracked.values()) URL.revokeObjectURL(url);
      locals.clear();
      tracked.clear();
    };
  }, []);

  const value = useMemo<UploadContextValue>(
    () => ({
      items,
      previews,
      trackedIds,
      enqueue,
      retry,
      cancel,
      dismiss,
      track,
      untrack,
    }),
    [items, previews, trackedIds, enqueue, retry, cancel, dismiss, track, untrack],
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUploads(): UploadContextValue {
  const context = useContext(UploadContext);
  if (!context) throw new Error("useUploads must be used inside an UploadProvider");
  return context;
}

/**
 * Adopt anything the server says is still in flight. Called once on app load so
 * that refreshing the page mid-classification doesn't lose the developing
 * cards.
 */
export function useResumeActiveEntries(active: Entry[] | undefined): void {
  const { track } = useUploads();

  useEffect(() => {
    if (!active) return;
    for (const entry of active) {
      if (isInFlight(entry.status)) track(entry);
    }
  }, [active, track]);
}
